import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Plan } from 'app/models/plan';
import { PlanPieceService } from 'app/services/PlanPiece/plan-piece.service';
import { AwscontrolService } from 'app/services/aws/awscontrol.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-plan-form',
  templateUrl: './plan-form.component.html',
  styleUrls: ['./plan-form.component.scss']
})
export class PlanFormComponent implements OnInit {
  form!: FormGroup;
  pieceId!: number;
  planId?: number;
  isEditMode = false;
  loading = false;
  uploadingImage = false;

  // Image handling
  selectedFile: File | null = null;
  imagePreview: string | ArrayBuffer | null = null;
  currentImageUrl: string | null = null; // existing image URL (for edit mode)
  newImageUrl: string | null = null;      // newly uploaded image URL

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private planService: PlanPieceService,
    private awsService: AwscontrolService
  ) {}

  ngOnInit(): void {
    this.pieceId = Number(this.route.snapshot.paramMap.get('pieceId'));
    this.planId = this.route.snapshot.paramMap.get('planId') ? Number(this.route.snapshot.paramMap.get('planId')) : undefined;
    this.isEditMode = !!this.planId;

    this.initForm();

    if (this.isEditMode && this.planId) {
      this.loadPlan();
    }
  }

  initForm(): void {
    this.form = this.fb.group({
      etage: [''],
      nbBeds: [0],
      nbBath: [0],
      sqft: [0],
      description: [''],
      prix: [''],
      videoUrl: ['']
    });
  }

  loadPlan(): void {
    this.loading = true;
    this.planService.getPlan(this.planId!).subscribe({
      next: (plan) => {
        this.form.patchValue({
          etage: plan.etage || '',
          nbBeds: plan.nbBeds || 0,
          nbBath: plan.nbBath || 0,
          sqft: plan.sqft || 0,
          description: plan.description || '',
          prix: plan.prix || '',
          videoUrl: plan.videoUrl || ''
        });

        // Set existing image if any
        if (plan.photosPlan && plan.photosPlan.length > 0) {
          this.currentImageUrl = plan.photosPlan[0];
          this.imagePreview = this.currentImageUrl;
        }

        this.loading = false;
      },
      error: () => {
        Swal.fire('Erreur', 'Impossible de charger le plan', 'error');
        this.loading = false;
      }
    });
  }

  // File selection handler
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];

      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result;
      };
      reader.readAsDataURL(this.selectedFile);
    }
  }

  // Clear selected file
  clearImage(): void {
    this.selectedFile = null;
    this.imagePreview = this.currentImageUrl; // revert to existing image if any
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  }

  // Upload image to S3
  uploadImageToS3(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.selectedFile) {
        // No new file selected; keep existing URL
        resolve(this.currentImageUrl || '');
        return;
      }

      this.uploadingImage = true;

      // If editing and there's an existing image that we're replacing, delete it first
      const deleteOldPromise = (this.isEditMode && this.currentImageUrl)
        ? this.awsService.deleteFile(this.currentImageUrl).toPromise().catch(err => {
            console.warn('Could not delete old image:', err);
            // Continue even if deletion fails
          })
        : Promise.resolve();

      deleteOldPromise.then(() => {
        this.awsService.uploadFile(this.selectedFile!, 'plans').subscribe({
          next: (response) => {
            this.uploadingImage = false;
            if (response.success && response.urls.length > 0) {
              resolve(response.urls[0]);
            } else {
              reject('Échec de l\'upload');
            }
          },
          error: (err) => {
            this.uploadingImage = false;
            reject(err);
          }
        });
      }).catch(err => {
        this.uploadingImage = false;
        reject(err);
      });
    });
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      return;
    }

    this.loading = true;

    try {
      // Upload image if a new one was selected
      let finalImageUrl = this.currentImageUrl;
      if (this.selectedFile) {
        finalImageUrl = await this.uploadImageToS3();
      }

      const formValue = this.form.value;
      const planData: Plan = {
        etage: formValue.etage,
        nbBeds: formValue.nbBeds,
        nbBath: formValue.nbBath,
        sqft: formValue.sqft,
        description: formValue.description,
        prix: formValue.prix,
        videoUrl: formValue.videoUrl,
        photosPlan: finalImageUrl ? [finalImageUrl] : []
      };

      const request = this.isEditMode && this.planId
        ? this.planService.updatePlan(this.planId, planData)
        : this.planService.createPlan(this.pieceId, planData);

      request.subscribe({
        next: () => {
          Swal.fire('Succès', `Plan ${this.isEditMode ? 'modifié' : 'créé'} avec succès`, 'success');
          this.router.navigate(['/piece', this.pieceId, 'plans']);
        },
        error: (err) => {
          console.error(err);
          Swal.fire('Erreur', 'Opération échouée', 'error');
          this.loading = false;
        }
      });

    } catch (error) {
      console.error(error);
      Swal.fire('Erreur', 'Échec de l\'upload de l\'image', 'error');
      this.loading = false;
    }
  }

  cancel(): void {
    this.router.navigate(['/piece', this.pieceId, 'plans']);
  }
}